import { useEffect, useRef } from 'react'
import { TimerDisplayServer } from './sequence/timer-display-server'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { nameTruncated } from '@/utils/helper'
import { SpeakingRole, TurnType, Winner } from '@db8/shared'
import useDarkMode from '@/hooks/useDarkMode'
import { useHall } from '@/contexts/HallProvider'
import { usePoll } from '@/contexts/PollProvider'
import { useTeamColors } from '@/hooks/useTeamColors'
import { useTurns } from '@/contexts/TurnsProvider'

export const TopInfoBar = () => {
  const { timer } = useTurns()

  const { getUser } = useHallUsers()
  const { winningTeam } = usePoll()

  const { hallMetadata } = useHall()
  const { bgClass } = useTeamColors({ speakingRole: hallMetadata?.winningTeam })

  const isDark = useDarkMode()

  const containerRef = useRef(null)

  useEffect(() => {
    if (hallMetadata.activeTurn > -1 && containerRef.current) {
      const el = document.getElementById(`turn-${hallMetadata.activeTurn}`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    }
  }, [hallMetadata.activeTurn])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const onWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        container.scrollLeft += e.deltaY
      }
    }

    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  const getBg = (isCompleted, isCurrentTurn, turn) => {
    const user = getUser(turn.primarySpeakerId)

    if (isCompleted) return 'bg-indigo-500 text-white border-indigo-500'
    else if (isCurrentTurn) {
      if (user?.speakingRole === SpeakingRole.Affirmative)
        return `${
          isDark ? 'gradient-bg-affirmative' : 'bg-gray-50 border-green-500 border-2 text-green-500'
        }  opacity-80`
      else if (user?.speakingRole === SpeakingRole.Negative)
        return `${
          isDark ? 'gradient-bg-negative' : 'bg-gray-50 border-blue-500 border-2 text-blue-500'
        } opacity-80`
      else if (turn.type === TurnType.Preperatory)
        return `${
          isDark
            ? 'gradient-bg-preperatory'
            : 'bg-gray-50 border-purple-500 border-2 text-purple-500'
        } opacity-80`
    } else return 'bg-gray-200 text-gray-700 border-gray-300'
  }

  return (
    <div className="px-3 flex w-full justify-center">
      <div
        className={`p-3 border rounded-md w-full flex justify-center ${
          winningTeam && `${bgClass} text-white`
        } ${hallMetadata?.winningTeam !== Winner.Undecided && 'mt-2'}`}
      >
        {hallMetadata?.winningTeam === Winner.Undecided ? (
          <div
            ref={containerRef}
            className="flex items-center justify-start overflow-x-auto noScrollbar"
          >
            {hallMetadata?.turns.map((turn, index) => {
              const isCompleted = turn.step < hallMetadata.activeTurn
              const isCurrentTurn = turn.step === hallMetadata.activeTurn

              return (
                <div className="flex items-center mb-2" id={`turn-${turn.step}`} key={turn.step}>
                  <div
                    className={`rounded-md border px-3 py-1 text-sm font-semibold text-center min-w-[125px] whitespace-nowrap
                    ${getBg(isCompleted, isCurrentTurn, turn)}
                  `}
                    title={turn.tooltip}
                  >
                    {isCurrentTurn ? (
                      <div className="flex flex-col font-bold dark:text-white">
                        <TimerDisplayServer duration={turn.duration} />
                        <p>{nameTruncated(getUser(turn.primarySpeakerId)?.displayName)}</p>
                      </div>
                    ) : (
                      turn.title
                    )}
                  </div>
                  {index < hallMetadata?.turns.length - 1 && (
                    <div
                      className={`h-1 w-2 ${
                        isCompleted ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-white'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <h5 className="text-center text-xl font-bold">
            {hallMetadata?.winningTeam === Winner.Tie ? winningTeam : `${winningTeam} has won this debate`}
          </h5>
        )}
      </div>
    </div>
  )
}
